/**
 * An operator's deletion of a resource. A VM asks for a reason and its exact name, and for the
 * scope: the full deletion (n8n then WiCloud) or WiCloud's record only, which calls nothing at n8n
 * and works with or without a provider UUID. A disk, network or bucket gets the plain confirm. On
 * the full deletion the API checks the live n8n state and matching expiry/billing-cycle dates; a
 * shutdown step appears only after it answers RESOURCE_RUNNING for an unexpired VM.
 */

import { useEffect, useId, useRef, useState } from "react";
import { Check, Copy, ExternalLink, HelpCircle, Power } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  DELETE_REASON_MAX_LENGTH,
  DELETE_REASON_MIN_LENGTH,
  RESTORE_WINDOW_HOURS,
  type DeleteResourcePayload,
} from "@/types/admin";
import {
  hasProviderVm,
  isRestorableDeletion as isRestorable,
} from "@/lib/providerVm";
import { APIError } from "@/lib/api";

/**
 * What the operator is deleting. "provider-and-record" is the ordinary deletion and the default;
 * "record-only" drops WiCloud data alone and leaves the machine running at the infrastructure.
 */
type DeleteScope = "provider-and-record" | "record-only";

/** The reason a fresh dialog opens with; the operator can still change it. */
const DEFAULT_REASON = "Clean up VM";

/** The backend's scope refusal, and the answer it points the operator to. */
const SCOPE_REFUSALS: Record<string, DeleteScope> = {
  VM_NOT_FOUND_ON_PROVIDER: "record-only",
  VM_UUID_MISSING: "record-only",
};

/** A scope refusal read off a failed delete, with the backend's own sentence. */
interface ScopeRefusal {
  code: string;
  message: string;
}

interface DeleteResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Name shown in the prompt and, for a VM, the string that has to be typed back. */
  resourceName: string;
  /** Backend ResourceType; only "VirtualMachine" needs the confirmation fields. */
  resourceType: string;
  /**
   * Current display status; the API decides eligibility from n8n state.
   */
  resourceStatus: string;
  /**
   * The recorded VM UUID, displayed in the confirmation.
   */
  providerResourceId?: string | null;
  /**
   * The VM's expiry. A VM whose term has ended is deleted directly, running or suspended, as the
   * Expired VMs page deletes it: resuming it is refused once the term is over.
   */
  expiresAt?: string | null;
  /** Owning tenant, shown so an operator can tell two similarly named VMs apart. */
  tenantName?: string;
  isPending: boolean;
  /**
   * Called with the body and the chosen mode: `recordOnly` sends it to the record endpoint
   * (`DELETE {id}/record`), which only a VM can take.
   */
  onConfirm: (payload: DeleteResourcePayload, recordOnly: boolean) => void;
  /**
   * The last failed delete's error (the mutation's `error`). Read only on the ordinary deletion, for
   * the scope refusals (VM_NOT_FOUND_ON_PROVIDER, VM_UUID_MISSING) and the shutdown step
   * (RESOURCE_RUNNING); every other failure is the caller's toast.
   */
  error?: unknown;
  /**
   * Shuts the VM down without leaving the dialog, offered when that is the next step. Pass the power
   * mutation's `mutateAsync`: the dialog waits on it, and a rejection is how it learns the step
   * failed. Omit it for an operator who may not run power actions — the step then has no button.
   */
  onShutDown?: () => Promise<unknown>;
  /** A power action on this VM is in flight, started here or anywhere else on the page. */
  isPowerActionPending?: boolean;
}

/** What has to happen to the VM before the full deletion can run, in order. */
type PrepStep = "shutdown";

const PREP_STEP_COPY: Record<
  PrepStep,
  { label: string; button: string; running: string }
> = {
  shutdown: {
    label: "Shut down",
    button: "Shut down",
    running: "Shutting down...",
  },
};

/**
 * Longest wait, after a step's request succeeded, for the new status to reach the dialog. The power
 * mutation refetches the row before it resolves, so this only bounds a refetch that failed.
 */
const STATUS_WAIT_MS = 15_000;

/**
 * Runs one preparation step at a time and holds the dialog still until it has landed.
 *
 * Three races it closes: a double click sending the shutdown twice (the ref is set in the same
 * tick as the click; `isPending` only arrives with the next render), the gap between the request
 * succeeding and the new status arriving, in which the button would come back and send a shutdown
 * to a VM already stopped, and the dialog being closed mid-step. The lock lifts when the status
 * moves, when the request fails, or after STATUS_WAIT_MS. Same mechanism as the client console's
 * DeleteVMDialog.
 */
function usePrepStepRunner(open: boolean, status: string) {
  const [inFlight, setInFlight] = useState<PrepStep | null>(null);
  const lockedRef = useRef(false);
  const statusAtStartRef = useRef(status);
  // Each run gets a number, so a stale timer from an earlier step never lifts a later step's lock.
  const runIdRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  const release = () => {
    lockedRef.current = false;
    window.clearTimeout(timerRef.current);
    setInFlight(null);
  };

  useEffect(() => {
    if (open) {
      release();
    }
  }, [open]);

  useEffect(() => {
    if (lockedRef.current && status !== statusAtStartRef.current) {
      release();
    }
  }, [status]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const run = async (step: PrepStep, action?: () => Promise<unknown>) => {
    if (!action || lockedRef.current) {
      return;
    }

    lockedRef.current = true;
    statusAtStartRef.current = status;
    runIdRef.current += 1;
    const runId = runIdRef.current;
    setInFlight(step);

    try {
      await action();
    } catch {
      // The mutation's own onError has already said why; the step is offered again.
      if (runId === runIdRef.current) {
        release();
      }
      return;
    }

    if (runId === runIdRef.current && lockedRef.current) {
      timerRef.current = window.setTimeout(() => {
        if (runId === runIdRef.current) {
          release();
        }
      }, STATUS_WAIT_MS);
    }
  };

  return { inFlight, run };
}

export function DeleteResourceDialog({
  open,
  onOpenChange,
  resourceName,
  resourceType,
  resourceStatus,
  providerResourceId,
  expiresAt,
  tenantName,
  isPending,
  onConfirm,
  error,
  onShutDown,
  isPowerActionPending = false,
}: DeleteResourceDialogProps) {
  const isVm = resourceType === "VirtualMachine";
  const reasonFieldId = useId();
  const nameFieldId = useId();
  const scopeFieldId = useId();

  // Opened filled in: the operator only reviews and confirms. The backend still checks the name.
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [typedName, setTypedName] = useState(resourceName);
  // Which of the two deletions this is. Reset on open to the full one, since record-only leaves a
  // machine behind and must not be inherited from a previous dialog; a VM with no real machine opens
  // on record-only, the only deletion it can take.
  const [scope, setScope] = useState<DeleteScope>(
    isVm && !hasProviderVm(providerResourceId)
      ? "record-only"
      : "provider-and-record",
  );
  const [scopeRefusal, setScopeRefusal] = useState<ScopeRefusal | null>(null);
  // Set by this dialog's own submit, so an `error` left over from an earlier request (another row)
  // is never read as the answer to this one.
  const submittedRef = useRef(false);

  // A failed delete leaves the dialog open with what was typed still in it; a fresh open starts
  // from the defaults for this resource, so the previous one's answers are never submitted again.
  useEffect(() => {
    if (open) {
      setReason(DEFAULT_REASON);
      setTypedName(resourceName);
      setScope(
        isVm && !hasProviderVm(providerResourceId)
          ? "record-only"
          : "provider-and-record",
      );
      setScopeRefusal(null);
      submittedRef.current = false;
    }
  }, [open]);

  // Runs once per failed request: a new attempt clears the mutation's error, and its failure is a
  // new object.
  useEffect(() => {
    if (!submittedRef.current || !(error instanceof APIError)) {
      return;
    }

    const suggested = SCOPE_REFUSALS[error.code];
    if (suggested === undefined) {
      return;
    }

    setScope(suggested);
    setScopeRefusal({ code: error.code, message: error.message });
  }, [error]);

  // n8n has no such machine, so the record-only answer leaves nothing running — the "keeps running"
  // warning below would be false.
  const machineUnknownToProvider =
    scopeRefusal?.code === "VM_NOT_FOUND_ON_PROVIDER" ||
    scopeRefusal?.code === "VM_UUID_MISSING";
  // What to call the thing in the copy. Every other type reaching this dialog is storage, a network
  // or a bucket, and "resource" covers all three without naming the wrong one.
  const noun = isVm ? "VM" : "resource";

  // These flags describe the stored row only; they never decide deletion eligibility.
  const isRunning =
    resourceType === "VirtualMachine" && resourceStatus === "Running";

  const isSuspended =
    resourceType === "VirtualMachine" &&
    resourceStatus === "Suspended" &&
    hasProviderVm(providerResourceId);

  // Only a VM has a scope: a disk, network or bucket always takes the ordinary deletion.
  const recordOnlyChosen = isVm && scope === "record-only";
  const fullDelete = !recordOnlyChosen;
  const leavesMachineBehind = recordOnlyChosen;

  // Mirrors the `restorable` branch of `AdminDeleteResourceCommandHandler` (the name predates the
  // withdrawal of restore): only a VM with a real machine, on the ordinary deletion, goes Terminating,
  // then Decommissioning once n8n has deleted it, and is kept for the deletion window before it is
  // purged. Everything else (record-only, a non-VM, a VM the provider never had) is gone the moment
  // this request answers.
  const isRestorableDeletion = isRestorable(
    resourceType,
    providerResourceId,
    fullDelete,
  );

  // Expiry is a display hint only; the API also requires the owning cycle to match.
  const expiryTime = expiresAt ? new Date(expiresAt).getTime() : null;
  const termEnded =
    isVm &&
    resourceStatus !== "Pending" &&
    expiryTime !== null &&
    Number.isFinite(expiryTime) &&
    expiryTime < Date.now();

  // Only the API's live provider check can require shutdown; local status may be stale. The steps
  // planned at that refusal stay on the stepper, so a finished one shows ticked.
  const [requiresShutdown, setRequiresShutdown] = useState(false);
  const [plannedSteps, setPlannedSteps] = useState<PrepStep[]>([]);
  useEffect(() => {
    if (error instanceof APIError && error.code === "RESOURCE_RUNNING") {
      setRequiresShutdown(true);
      setPlannedSteps(["shutdown"]);
    }
  }, [error]);
  // The step is done once the shutdown was sent AND the VM reads Stopped: a form unlocked when the
  // request returns lets a quick resubmit hit RESOURCE_RUNNING again.
  const [shutdownSent, setShutdownSent] = useState(false);
  useEffect(() => {
    if (requiresShutdown && shutdownSent && resourceStatus === "Stopped") {
      setRequiresShutdown(false);
      setShutdownSent(false);
    }
  }, [requiresShutdown, shutdownSent, resourceStatus]);
  useEffect(() => {
    if (open) {
      setRequiresShutdown(false);
      setShutdownSent(false);
      setPlannedSteps([]);
    }
  }, [open]);
  const isBlocked = fullDelete && requiresShutdown;
  const pendingSteps: PrepStep[] = isBlocked ? ["shutdown"] : [];
  const nextStep: PrepStep | undefined = pendingSteps[0];
  const nextStepAction = onShutDown
    ? async () => {
        await onShutDown();
        setShutdownSent(true);
      }
    : undefined;

  const stepRunner = usePrepStepRunner(open, resourceStatus);
  // Everything else in the dialog waits while the VM is changing state under it.
  const isStepBusy =
    (stepRunner.inFlight !== null && requiresShutdown) || isPowerActionPending;
  const isLocked = isPending || isStepBusy;
  const busyStep = stepRunner.inFlight ?? nextStep;

  // VMs only, record-only included: a disk, network or bucket needs no reason or typed name.
  const needsConfirmation = isVm;
  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < DELETE_REASON_MIN_LENGTH;
  // Case-sensitive, like the backend.
  const nameMatches = typedName.trim() === resourceName;
  const canDelete =
    !isLocked &&
    !isBlocked &&
    (!needsConfirmation || (!reasonTooShort && nameMatches));

  const handleConfirm = () => {
    if (!canDelete) {
      return;
    }

    submittedRef.current = true;
    // Storage, networks and buckets carry neither field; the backend requires both for a VM only.
    const payload = needsConfirmation
      ? { reason: trimmedReason, confirmationName: typedName.trim() }
      : {};
    onConfirm(payload, !fullDelete);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Cancel is disabled while a request or a step runs, so closing must be blocked too.
        if (isLocked && !next) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          if (isLocked) {
            event.preventDefault();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this resource?</AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogBody className="space-y-4">
          <AlertDialogDescription className="break-words">
            {`"${resourceName}"`}
            {tenantName ? ` (${tenantName})` : ""}
            {/* One sentence per branch: a restorable VM, anything else destroyed outright, and the
                record-only branch that destroys nothing at the infrastructure. */}
            {fullDelete
              ? isRestorableDeletion
                ? ` is deleted at the infrastructure and can be restored for ${RESTORE_WINDOW_HOURS}h. Snapshots and firewall rules are not restored.`
                : ` will be permanently deleted.${isVm ? " All data on this VM is lost." : ""}`
              : " is removed from WiCloud only; nothing is deleted at the infrastructure."}
          </AlertDialogDescription>
          {/* Asked before anything else, because the two notes below only apply to one of the
            answers and the fields below are collected for both. */}
          {isVm && (
            <div className="space-y-2">
              <RadioGroup
                aria-label="What should be deleted?"
                value={scope}
                onValueChange={(next) => setScope(next as DeleteScope)}
                disabled={isLocked}
                className="gap-3"
              >
                <label
                  htmlFor={`${scopeFieldId}-both`}
                  className="flex cursor-pointer items-start gap-3"
                >
                  <RadioGroupItem
                    value="provider-and-record"
                    id={`${scopeFieldId}-both`}
                    className="mt-0.5 cursor-pointer"
                  />
                  <span className="text-sm leading-snug">
                    <span className="font-medium">{`Delete ${noun}`}</span>
                  </span>
                </label>
                <label
                  htmlFor={`${scopeFieldId}-record`}
                  className="flex cursor-pointer items-start gap-3"
                >
                  <RadioGroupItem
                    value="record-only"
                    id={`${scopeFieldId}-record`}
                    className="mt-0.5 cursor-pointer"
                  />
                  <span className="text-sm leading-snug">
                    <span className="font-medium">The WiCloud record only</span>
                    <span className="block text-muted-foreground">
                      {`Use when the ${noun} is already gone from the infrastructure or it refuses to delete.`}
                    </span>
                  </span>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* The whole reason this answer is not the default. Two different facts, and quoting the
            wrong one is worse than saying nothing: with a machine at the provider it is left
            running and someone has to go and remove it, and the uuid is the only handle they will
            have once the record is gone. With no machine there, nothing is left behind at all —
            which is exactly the case this option was added for. */}
          {/* Why the answer above changed. Kept up after a manual switch too: it is still the
            backend's last word on this machine. */}
          {scopeRefusal && (
            <div
              role="alert"
              className="space-y-1 rounded-md border border-warn/40 bg-warn-soft p-3 text-sm"
            >
              <p className="font-medium text-warn-ink">
                {scopeRefusal.message}
              </p>
              <p className="text-foreground">
                {machineUnknownToProvider
                  ? `The infrastructure has no machine for this ${noun}, so there is nothing there to delete. "The WiCloud record only" is now selected — confirm to clear this row.`
                  : `The machine is still at the infrastructure, so removing only the record would leave it running untracked. "Delete ${noun}" is now selected — confirm to delete it normally.`}
              </p>
            </div>
          )}

          {recordOnlyChosen &&
            !machineUnknownToProvider &&
            (leavesMachineBehind && hasProviderVm(providerResourceId) ? (
              <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive-ink">
                  {`The ${noun} will keep running at the infrastructure.`}
                </p>
                <p className="text-foreground">
                  WiCloud stops billing it and nothing here points at it any
                  more, so it has to be removed by hand. Copy the machine ID
                  first — it is the only handle left once the record is gone.
                </p>
                {/* A button, not just selectable text: the sentence above tells the operator to copy
                  this, and it is about to become the only way to find the machine again. */}
                <div className="flex items-start gap-2">
                  <p className="break-all font-mono text-xs text-foreground">
                    {providerResourceId}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label="Copy machine ID"
                    onClick={() => {
                      navigator.clipboard.writeText(providerResourceId ?? "");
                      toast.success("Machine ID copied");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
                {`This ${noun} has no machine recorded at the infrastructure, so there is nothing there to leave behind — full deletion requires a VM the infrastructure still knows. Record-only deletion removes WiCloud data without calling the infrastructure.`}
              </p>
            ))}

          {termEnded && fullDelete && (isRunning || isSuspended) && (
            <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
              {`An expired VM can be deleted without a shutdown when its expiry and billing-cycle dates match. The dates are checked before deletion.`}
            </p>
          )}

          {plannedSteps.length > 0 && fullDelete && (
            <div className="space-y-2">
              <p className="text-sm text-foreground">
                {isBlocked
                  ? "The infrastructure reports this VM is still running. Check it and shut it down before deleting it."
                  : "The shutdown request finished. Confirm deletion to recheck the machine."}
              </p>
              <DeleteStepper steps={plannedSteps} pendingSteps={pendingSteps} />
            </div>
          )}

          {needsConfirmation && !isBlocked && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={reasonFieldId}>
                  Reason for deletion{" "}
                  <span className="text-destructive-ink" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Textarea
                  id={reasonFieldId}
                  required
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={`Why is this ${noun} being deleted?`}
                  maxLength={DELETE_REASON_MAX_LENGTH}
                  rows={3}
                  disabled={isLocked}
                  aria-invalid={reason.length > 0 && reasonTooShort}
                />
                {reason.length > 0 && reasonTooShort && (
                  <p className="text-xs text-muted-foreground">
                    {`At least ${DELETE_REASON_MIN_LENGTH} characters.`}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                {/* Same shape as the client's DeleteVMDialog: the name sits in the label (break-all,
                  so a long name wraps), its copy button beside it. */}
                <div className="flex items-center gap-1">
                  <Label htmlFor={nameFieldId} className="min-w-0 break-all">
                    Type{" "}
                    <code className="rounded bg-panel-2 px-1 py-0.5 font-mono text-[0.8125rem] text-foreground">
                      {resourceName}
                    </code>{" "}
                    to confirm
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground"
                    onClick={() => {
                      void navigator.clipboard.writeText(resourceName);
                      toast.success(
                        `${noun[0].toUpperCase()}${noun.slice(1)} name copied`,
                      );
                    }}
                    title={`Copy the ${noun} name`}
                    aria-label={`Copy the ${noun} name`}
                    disabled={isLocked}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </div>
                <Input
                  id={nameFieldId}
                  className="font-mono"
                  value={typedName}
                  onChange={(event) => setTypedName(event.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={isLocked}
                  aria-invalid={typedName.length > 0 && !nameMatches}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleConfirm();
                    }
                  }}
                />
                {typedName.length > 0 && !nameMatches && (
                  <p className="text-xs text-destructive">
                    {`The name you typed does not match the ${noun} name.`}
                  </p>
                )}
              </div>
            </div>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          {/* A VM's deletion only: that is the one with a job, retries and a deletion window behind
              it. A popover, so reading it never closes a dialog the operator is half-way through. */}
          {isVm && <DeleteHelp />}
          <AlertDialogCancel disabled={isLocked}>Cancel</AlertDialogCancel>
          {/* The next step sits beside Delete, so the operator's hand never moves. */}
          {fullDelete && nextStep !== undefined && nextStepAction && (
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => void stepRunner.run(nextStep, nextStepAction)}
              disabled={isStepBusy}
            >
              {isStepBusy ? (
                <Spinner />
              ) : (
                <Power className="h-4 w-4" aria-hidden />
              )}
              {isStepBusy && busyStep
                ? PREP_STEP_COPY[busyStep].running
                : PREP_STEP_COPY[nextStep].button}
            </Button>
          )}
          {/* A plain Button, not AlertDialogAction: the action closes the dialog on click, and
              this one must stay open while the request is in flight and when it fails. */}
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canDelete}
          >
            {isPending && <Spinner className="mr-2" />}
            {/* The caption names the answer that is selected. "Delete" over a record-only
                deletion would promise the machine goes too. */}
            {isPending
              ? fullDelete
                ? "Deleting..."
                : "Removing record..."
              : fullDelete
                ? "Delete"
                : "Remove record"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * What happens after "Delete" on a VM, opened in place. The long version, with every refusal and
 * audit row, is `/docs/vm-delete.html`.
 */
function DeleteHelp() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="sm:mr-auto">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="max-h-[70vh] w-96 space-y-3 overflow-y-auto text-sm"
      >
        <p className="text-muted-foreground">
          For a VM with a machine at the infrastructure. Nothing is removed from
          WiCloud's database; the row only changes status.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">After Delete</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              · the VM, its disks and networks read <b>Terminating</b> until n8n
              confirms; power, reinstall, expiry, update by UUID and delete are
              locked meanwhile
            </li>
            <li>
              · then they read <b>Decommissioning</b> with a trash icon for{" "}
              {RESTORE_WINDOW_HOURS}h — hidden from the default Active view,
              shown under Status = Decommissioning — and can be restored on the
              same uuid and IP, with empty disks
            </li>
            <li>
              · after that window a scheduled job finalizes them to Terminated;
              if it never runs, use <b>Finalize deletion now</b>
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">If n8n does not confirm</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· retried after 1, 3 and 5 minutes — four tries in all</li>
            <li>
              · before counting a refusal, WiCloud asks n8n whether it still has
              the VM; if it does not, the deletion is done
            </li>
            <li>
              · after the fourth refusal the VM goes back to the status it had,
              with a "last deletion did not complete" banner
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">Refused before anything changes</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              · the VM's expiry or billing cycle is missing, or their end dates
              differ — an admin must correct the data first
            </li>
            <li>
              · an unexpired VM is running at n8n — turn it off first. Stopped
              and Suspended VMs can be deleted; expired VMs need no shutdown
            </li>
            <li>
              · n8n does not know the VM — delete the WiCloud record only — or
              does not answer
            </li>
            <li>
              · a renewal transfer is awaiting verification — settle it first
            </li>
          </ul>
        </div>

        <p className="text-muted-foreground">
          An unpaid renewal order stays open but cannot be paid while the VM is
          deleted.
        </p>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href="/docs/vm-delete.html" target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One line of numbered steps ending on Delete: a tick once a step is done, the next one in amber,
 * the rest muted. Same shape as the client console's delete stepper.
 */
function DeleteStepper({
  steps,
  pendingSteps,
}: {
  steps: PrepStep[];
  pendingSteps: PrepStep[];
}) {
  const all: (PrepStep | "delete")[] = [...steps, "delete"];
  const current = pendingSteps[0] ?? "delete";

  return (
    <ol className="flex items-center gap-1.5 text-xs">
      {all.map((step, index) => {
        const done = step !== "delete" && !pendingSteps.includes(step);
        const isCurrent = step === current;

        return (
          // Every step after the first carries the line leading into it, so the steps spread
          // evenly and the list holds only real steps for a screen reader.
          <li
            key={step}
            className={
              index > 0
                ? "flex min-w-0 flex-1 items-center gap-1.5"
                : "flex shrink-0 items-center gap-1.5"
            }
            aria-current={isCurrent ? "step" : undefined}
          >
            {index > 0 && (
              <span
                aria-hidden
                className="mr-0.5 h-px min-w-3 flex-1 bg-hairline"
              />
            )}
            <span
              className={
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-semibold tabular-nums " +
                (done
                  ? "bg-ok-soft text-ok"
                  : isCurrent
                    ? "bg-warn-soft text-warn"
                    : "bg-panel-2 text-muted-foreground")
              }
            >
              {done ? (
                <Check className="h-3 w-3" aria-label="Done" />
              ) : (
                index + 1
              )}
            </span>
            <span
              className={
                isCurrent
                  ? "shrink-0 whitespace-nowrap font-medium text-foreground"
                  : "shrink-0 whitespace-nowrap text-muted-foreground"
              }
            >
              {step === "delete" ? "Delete" : PREP_STEP_COPY[step].label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
